import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {DownloadIcon} from '../components/Icons';
import {MAPPER_PLATFORMS, detectRecommendedPlatform, getMapperDownloadUrl, type MapperPlatform} from '../lib/builds';

/**
 * Public download page for CensorMe Mapper, the desktop companion app. Links go straight to the
 * static-builds-worker (`GET /windows`, `/mac-arm64`, `/mac-x64`), which streams the installer with
 * `Content-Disposition: attachment` — a plain `<a href>` is all that's needed, no fetch/blob and no
 * version number to track from here. See docs/BACKEND_REQUESTS.md for the worker's behaviour.
 */
const DownloadsPage: React.FC = () => {
  // Read after mount, not during render: this page is prerendered with no user agent, and reading it
  // in render would make the first client render disagree with the prerendered HTML.
  const [recommended, setRecommended] = useState<MapperPlatform | null>(null);

  useEffect(() => {
    setRecommended(detectRecommendedPlatform());
  }, []);

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8 w-full">
      <div className="prose prose-invert max-w-none">
        <h1 className="text-4xl font-bold text-white mb-2">Download CensorMe Mapper</h1>
        <p className="text-gray-400 mb-8">
                    CensorMe Mapper is the desktop companion app used to fix broken filepaths in XML. Pick your platform below to get the
                    latest build — no account or sign-in needed.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {MAPPER_PLATFORMS.map(({platform, label}) => {
          const isRecommended = platform === recommended;

          return (
            <a
              key={platform}
              href={getMapperDownloadUrl(platform)}
              className={`flex flex-col items-center gap-3 rounded-lg border p-6 text-center transition-colors ${
                isRecommended
                  ? 'border-primary-500 bg-gray-800'
                  : 'border-gray-700 bg-gray-800 hover:border-gray-600'
              }`}
            >
              {isRecommended && (
                <span className="rounded-full bg-primary-600 px-2 py-0.5 text-xs font-medium text-white">
                                    Recommended for your system
                </span>
              )}
              <DownloadIcon className="h-8 w-8 text-gray-300" />
              <span className="text-lg font-semibold text-white">{label}</span>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-sm text-white hover:bg-primary-700 transition-colors">
                                Download
              </span>
            </a>
          );
        })}
      </div>

      <p className="mt-6 text-sm text-gray-400">
                If a download doesn&apos;t start, that platform may not have a build published yet —{' '}
        <Link to="/contact" className="text-blue-400 hover:text-blue-300 underline transition-colors">
                    let us know
        </Link>
        {' '}and we&apos;ll sort it out.
      </p>
    </div>
  );
};

export default DownloadsPage;
